/**
 * deathScreen.ts — Canvas-rendered "RIP PEANUT" death screen
 * =====================================================================
 * BETA 15.93 — From PSBOT RUN handoff (2026-05-05-death-screen-rip-peanut-spec.md)
 * + Dylan's grade/insult overlay + REPLAY/QUIT buttons after 3 seconds.
 *
 * Replaces the legacy renderGameOverScreen. Same activation phase
 * (state.phase === 'gameover') but a much fuller experience:
 *   - Black background with slow-drifting white star particles
 *   - "RIP [CHARACTER NAME]" pulsing red title
 *   - Grade badge (e.g., "F — ABSOLUTE TRASH") below the title
 *   - Player sprite shown as a memorial portrait
 *   - Stats list (uses the existing stat pile from the legacy screen)
 *   - Burnt Peanut trash-talk lines cycling every ~10 seconds
 *   - REPLAY / QUIT buttons appear at 3 seconds, clickable through the
 *     entire eulogy duration
 *
 * Audio: eulogy is fired separately from engine.ts on gameover entry
 * (via playDeathEulogy in audio.ts). This module only renders pixels +
 * exposes button hit zones for the click handler.
 *
 * Star particles use a deterministic-ish seed so they don't shimmer
 * randomly between frames; same seed every frame for the same death.
 * =====================================================================
 */

import type { GameState } from './types';
import { computeGrade, GRADE_LABELS, type Grade } from './deathGrade';
import { selectTrashLines, type TrashLine } from './deathTrashTalk';
import { getRunDamageDealt } from './weaponAdapter';
// BETA 16.93 — Controller phase 3: read focused REPLAY/QUIT id so the
// focus box overlays the gamepad-targeted button.
import { getFocusedTarget, getActiveScreen } from './gamepadFocus';

// ─── Star field (deterministic seed, computed once per death) ─────────

interface Star {
  // BETA 16.07 — Hyperspace travel: stars drift outward from screen center
  // along a fixed angle, getting bigger and brighter as they approach the
  // edges. When a star passes the edge it respawns at center with a new
  // random angle. Mimics the "flying forward through space" effect.
  // `t` is normalized progress 0..1+ from center to edge along the angle.
  // `speed` is how fast t advances per second (0.18..0.42 for variety).
  // `angle` is the radial direction from center.
  // `baseSize` is the minimum dot size at t=0 (center).
  // `seed` is per-star randomness for slight twinkle variation.
  t: number;
  speed: number;
  angle: number;
  baseSize: number;
  seed: number;
}

let _stars: Star[] = [];
let _starsSeed = -1;
let _starsLastElapsed = 0;
let _trashLines: TrashLine[] = [];
let _trashLinesSeed = -1;

/** Reset the cached star field + trash lines so the next death gets a
 *  fresh selection. Called from engine.ts on initGame. */
export function resetDeathScreenState(): void {
  _stars = [];
  _starsSeed = -1;
  _starsLastElapsed = 0;
  _trashLines = [];
  _trashLinesSeed = -1;
}

function generateStars(seed: number): Star[] {
  // Simple xorshift PRNG for stable placement
  let s = seed | 0; if (s === 0) s = 1;
  const rand = () => {
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
    return (Math.abs(s) % 10000) / 10000;
  };
  // BETA 16.07 — Hyperspace fly-forward: each star is parameterized by
  // angle + radial-progress + speed. Stars are scattered along the
  // travel axis (varied initial t) so the field is full at frame 0
  // instead of starting empty at center.
  const count = 100;
  const out: Star[] = [];
  for (let i = 0; i < count; i++) {
    out.push({
      t: rand(),                          // initial progress 0..1
      speed: 0.18 + rand() * 0.24,         // 0.18..0.42 progress/sec — variety = depth
      angle: rand() * Math.PI * 2,
      baseSize: 0.4 + rand() * 0.6,        // tiny at center
      seed: rand() * 1000,
    });
  }
  return out;
}

// ─── Button hit zones (exposed for click handler) ─────────────────────

export interface DeathScreenButtonRects {
  replay: { x: number; y: number; w: number; h: number };
  quit:   { x: number; y: number; w: number; h: number };
  /** True once 3 seconds have elapsed and buttons are visible/clickable. */
  visible: boolean;
}

const BUTTON_DELAY_SEC = 3;

export function getDeathScreenButtonRects(canvasW: number, canvasH: number, elapsedSec: number): DeathScreenButtonRects {
  const visible = elapsedSec >= BUTTON_DELAY_SEC;
  // Middle-bottom area, side by side. Sized for both desktop + mobile.
  const btnW = Math.min(220, canvasW * 0.22);
  const btnH = 56;
  const gap  = 32;
  const totalW = btnW * 2 + gap;
  const startX = (canvasW - totalW) / 2;
  // y position: ~85% down the screen so it doesn't cover trash talk
  // (which sits around 65-78% per the renderer below) and isn't pinned
  // to the very bottom edge.
  // BETA 19.18 — Clamp Y so the button bottom always sits at least 16px
  // above the canvas edge. The 0.85 baseline put the button bottom 3px
  // from the edge on iPhone landscape (852x393) — close enough to be
  // covered by Safari's URL bar. The trash talk lives in 0.10-0.52 of
  // the canvas, so clamping up to ~h-72 stays well clear of it on any
  // viewport the death screen can reasonably render in.
  const naturalY = canvasH * 0.85;
  const maxY = canvasH - btnH - 16;
  const y = Math.min(naturalY, Math.max(0, maxY));
  return {
    replay: { x: startX, y, w: btnW, h: btnH },
    quit:   { x: startX + btnW + gap, y, w: btnW, h: btnH },
    visible,
  };
}

// ─── Main render function ─────────────────────────────────────────────

// BETA 16.00 — Diagnostic: log once per death so we can confirm the
// renderer is reached + see the elapsed time we're computing.
let _renderLoggedForGameOverTs = 0;

export function renderDeathScreen(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  w: number,
  h: number,
  playerSprite: HTMLImageElement | null,
): void {
  const elapsed = state.gameOverStartTime > 0
    ? (Date.now() - state.gameOverStartTime) / 1000
    : 0;

  // Log ONCE per gameover (keyed by gameOverStartTime) so console isn't
  // spammed per frame. Tells us: was renderer reached? what's elapsed?
  // are state fields populated?
  if (state.gameOverStartTime > 0 && _renderLoggedForGameOverTs !== state.gameOverStartTime) {
    _renderLoggedForGameOverTs = state.gameOverStartTime;
    console.log('[deathScreen] FIRST FRAME — elapsed=', elapsed.toFixed(2),
      'character=', state.character?.id, '/', state.character?.name,
      'time=', state.time, 'killCount=', state.killCount,
      'killedByType=', state.killedByType, 'playerSprite=', !!playerSprite,
      'canvas wxh=', w, 'x', h);
  }

  ctx.save();

  // ── Black background ──
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, w, h);

  // ── Star particles — HYPERSPACE TRAVEL effect ──
  // BETA 16.07 — Per Dylan: "can we make the stars in the background look
  // like we are floating through space. some movement like we are
  // traving forward?" Each star has a fixed angle from screen center +
  // radial progress (t). Each frame, t advances by speed*dt. Stars near
  // center are tiny and dim; stars near the edges are bigger, brighter,
  // and faster (illusion of forward motion). When t > 1.05 the star
  // wraps back to center with a new random angle. Stars are drawn as
  // streaks (line from prior position to current) to enhance motion.
  const wantStarSeed = 1; // single fixed seed — no need to regenerate per resize
  if (_starsSeed !== wantStarSeed) {
    _stars = generateStars(wantStarSeed);
    _starsSeed = wantStarSeed;
    _starsLastElapsed = 0;
  }
  // Compute frame delta for this render frame. Clamped to avoid huge
  // jumps when the tab was backgrounded.
  const starDt = Math.max(0, Math.min(0.1, elapsed - _starsLastElapsed));
  _starsLastElapsed = elapsed;

  const cx = w / 2;
  const cy = h / 2;
  // Reach distance — corner-to-center diagonal so stars wrap at the corners.
  const maxReach = Math.sqrt(cx * cx + cy * cy);
  for (const st of _stars) {
    // Advance progress
    st.t += st.speed * starDt;
    if (st.t > 1.05) {
      // Wrap back to center with a new angle
      st.t = 0;
      // xorshift in-place for new angle
      let s = (st.seed | 0) ^ Math.round(elapsed * 1000);
      s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
      st.angle = ((Math.abs(s) % 10000) / 10000) * Math.PI * 2;
      st.seed = s;
    }
    // Position along the radial line from center
    const dist = st.t * maxReach;
    const sx = cx + Math.cos(st.angle) * dist;
    const sy = cy + Math.sin(st.angle) * dist;
    if (sx < -10 || sx > w + 10 || sy < -10 || sy > h + 10) continue; // off-screen
    // Size + brightness scale with progress (hyperspace illusion)
    const size = st.baseSize + st.t * 1.6;          // 0.4 → ~2.0 px
    const alpha = Math.min(1, 0.25 + st.t * 0.85);  // fade in as it travels out
    // Draw the star as a tiny streak from a slightly-earlier position
    // to current position — gives motion blur without per-frame state.
    const trailDist = Math.min(8, st.t * st.speed * 35);
    const tx = cx + Math.cos(st.angle) * (dist - trailDist);
    const ty = cy + Math.sin(st.angle) * (dist - trailDist);
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(tx, ty);
    ctx.lineTo(sx, sy);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  ctx.lineWidth = 1;

  // BETA 15.95 — Wrap the rest of the renderer in try/catch so a runtime
  // error doesn't silently bail the function and leave the player on a
  // bare starfield. Per Dylan: "i see the outer space. i dont see my
  // character or any rate=ing. the only thing i see is the outerspace."
  // Stars rendered fine but everything after silently failed. The catch
  // logs to console + draws a visible error banner so we can see what
  // broke without console access.
  try {
    _renderDeathScreenBody(ctx, state, w, h, playerSprite, elapsed);
  } catch (err) {
    const msg = (err instanceof Error) ? err.message : String(err);
    console.error('[deathScreen] render failed:', err);
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#ff3333';
    ctx.font = `bold 24px "Share Tech Mono", monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('DEATH SCREEN ERROR — see console', w / 2, h * 0.45);
    ctx.font = `16px "Share Tech Mono", monospace`;
    ctx.fillStyle = '#ffaaaa';
    ctx.fillText(msg.slice(0, 200), w / 2, h * 0.50);
  }
  ctx.restore();
}

function _renderDeathScreenBody(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  w: number,
  h: number,
  playerSprite: HTMLImageElement | null,
  elapsed: number,
): void {

  // ── Compute grade + cache trash talk lines ──
  const survivalSeconds = state.time ?? 0;
  const killCount = state.killCount ?? 0;
  const levelReached = state.player?.level ?? 1;
  const damageDealt = Math.round(getRunDamageDealt() ?? 0);
  // Death cause normalized: bare type for filter matching.
  const causeType = state.killedByType ?? 'unknown';
  const characterId = state.character?.id ?? 'unknown';
  const characterName = state.character?.name ?? 'PEANUT';
  const { grade } = computeGrade({
    survivalSeconds, killCount, levelReached, damageDealt,
    deathCause: state.phase === 'won' ? 'won' : `enemy:${causeType}`,
  });
  // Cache trash lines per death (seed by survival time + kills + character)
  const trashSeed = Math.round(survivalSeconds * 100) ^ killCount ^ characterId.length;
  if (_trashLinesSeed !== trashSeed) {
    _trashLines = selectTrashLines({
      survivalSeconds, killCount,
      character: characterId,
      deathCause: state.phase === 'won' ? 'won' : causeType,
      grade,
    }, 6);
    _trashLinesSeed = trashSeed;
  }

  // BETA 16.05 — Per Dylan: "the words are all jacked up and covering up
  // each other... i sent playing in wide and playing in 16:9". Root cause:
  // font sizes were `w * X` (% of canvas WIDTH), but y-positions were
  // `h * Y` (% of canvas HEIGHT). On wide / ultrawide screens, w >> h,
  // so fonts grew massive while y-spacing stayed tight — overlapping.
  // Fix: size fonts off `Math.min(w, h)` (the smaller dimension) so they
  // never outgrow the screen. Plus more vertical breathing room between
  // every element.
  const minDim = Math.min(w, h);

  // ── Title — "RIP [CHARACTER]" (death, red) or "EXTRACTED — [CHARACTER]"
  // (success, green) when this run ended by reaching the extraction port. ──
  const _extracted = !!state.extracted;
  const titleAlpha = Math.min(1, elapsed / 1.0);
  const pulse = 0.85 + 0.15 * Math.sin(elapsed * 2.5);
  ctx.globalAlpha = titleAlpha;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = _extracted ? '#15c98a' : '#cc0000';
  ctx.shadowBlur = 50 * pulse;
  ctx.font = `bold ${Math.round(minDim * 0.075)}px "Share Tech Mono", monospace`;
  if (_extracted) {
    ctx.fillStyle = `rgb(${Math.round(80 + 40 * pulse)}, ${Math.round(225 + 30 * pulse)}, ${Math.round(150 + 30 * pulse)})`;
    ctx.fillText(`EXTRACTED — ${characterName.toUpperCase()}`, w / 2, h * 0.10);
  } else {
    ctx.fillStyle = `rgb(${Math.round(220 + 35 * pulse)}, ${Math.round(20 * pulse)}, ${Math.round(20 * pulse)})`;
    ctx.fillText(`RIP ${characterName.toUpperCase()}`, w / 2, h * 0.10);
  }
  ctx.shadowBlur = 0;

  // ── Score headline + grade badge — appears at ~1.2s ──
  // BETA 21.12 — Dylan: "the death screen does not show our score." It DID, but
  // only as a tiny 'GRADE SCORE' line buried LAST in the stats block (19px,
  // appears 4.2s in) — so it read as missing. Promote the SCORE to the big
  // headline number right under RIP, with the grade letter + insult beneath it.
  // (Removed the now-redundant 'GRADE SCORE' row from the stats list below.)
  // Bounds: RIP title bottom ≈ 0.14h; this headline centered at 0.195h (half-
  // height ≈ 0.029h → top ≈ 0.166h, clear of RIP); grade line 0.255h; subtitle
  // 0.30h; sprite top ≈ 0.35h — no overlap (UI-overlap check, computed).
  const badgeAlpha = Math.max(0, Math.min(1, (elapsed - 1.2) / 0.8));
  if (badgeAlpha > 0) {
    const labels = GRADE_LABELS[grade];
    ctx.globalAlpha = badgeAlpha;
    // BETA 21.80 — Dylan: "score 53/100 is lame, I don't even know what it means —
    // it needs to be what's on the leaderboard." Headline is now the REAL run stats
    // (kills · damage · time survived). The grade letter + insult stay below as flavor.
    ctx.shadowColor = labels.color;
    ctx.shadowBlur = 24;
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${Math.round(minDim * 0.040)}px "Share Tech Mono", monospace`;
    const _hlMin = Math.floor(survivalSeconds / 60);
    const _hlSec = Math.floor(survivalSeconds) % 60;
    ctx.fillText(`${killCount} KILLS  ·  ${damageDealt.toLocaleString()} DMG  ·  ${_hlMin}:${String(_hlSec).padStart(2, '0')}`, w / 2, h * 0.195);
    ctx.shadowBlur = 0;
    if (_extracted) {
      // Success flavor — no grade-shaming on a clean getaway.
      ctx.fillStyle = '#48e0a0';
      ctx.font = `bold ${Math.round(minDim * 0.030)}px "Share Tech Mono", monospace`;
      ctx.fillText('LOOT SECURED — scrap, cases & modded guns kept', w / 2, h * 0.255);
    } else {
      // Grade letter + insult title — flavor line beneath the score
      ctx.fillStyle = labels.color;
      ctx.font = `bold ${Math.round(minDim * 0.030)}px "Share Tech Mono", monospace`;
      ctx.fillText(`GRADE ${grade} — "${labels.title}"`, w / 2, h * 0.255);
      // Subtitle — smaller flavor
      ctx.fillStyle = '#888888';
      ctx.font = `italic ${Math.round(minDim * 0.020)}px "Share Tech Mono", monospace`;
      ctx.fillText(labels.subtitle, w / 2, h * 0.300);
    }
  }
  ctx.globalAlpha = 1;

  // ── Character sprite (memorial portrait) ──
  // BETA 16.07 — Per Dylan: "character sprite should not be streached or
  // warped in death scren." Was drawn as a fixed square (sprSize × sprSize),
  // which stretched non-square character sprites. Now: respect natural
  // aspect ratio. Compute scale so the larger dimension fits in sprSize.
  if (playerSprite) {
    const sprAlpha = Math.max(0, Math.min(1, (elapsed - 1.8) / 1.0));
    if (sprAlpha > 0) {
      const sprMax = minDim * 0.14;
      const natW = playerSprite.naturalWidth || playerSprite.width || sprMax;
      const natH = playerSprite.naturalHeight || playerSprite.height || sprMax;
      const scale = sprMax / Math.max(natW, natH);
      const drawW = natW * scale;
      const drawH = natH * scale;
      ctx.globalAlpha = sprAlpha * 0.85;
      ctx.drawImage(
        playerSprite,
        w / 2 - drawW / 2,
        h * 0.42 - drawH / 2,
        drawW, drawH,
      );
      ctx.globalAlpha = 1;
    }
  }

  // ── "Killed by X" subtitle — small, under sprite (death only; no killer on a clean extract) ──
  const killerAlpha = Math.max(0, Math.min(1, (elapsed - 2.0) / 0.6));
  if (killerAlpha > 0 && state.killedByName && !_extracted) {
    ctx.globalAlpha = killerAlpha;
    ctx.fillStyle = '#aa3333';
    ctx.font = `${Math.round(minDim * 0.022)}px "Share Tech Mono", monospace`;
    ctx.fillText(`Killed by ${state.killedByName}`, w / 2, h * 0.52);
    ctx.globalAlpha = 1;
  }
  // ── BETA 22.48 — DEATH CLARITY line (Dylan: "i never see an enemy hit me
  // when i die. its very confusing"). Spells out HOW + WHEN the lethal hit
  // landed; phantom deaths (no fresh hit) glow orange so they stand out. ──
  if (killerAlpha > 0 && state.deathExplanation && !_extracted) {
    ctx.globalAlpha = killerAlpha;
    ctx.fillStyle = state.deathWasPhantom ? '#ffb347' : '#9a8d8d';
    ctx.font = `${Math.round(minDim * 0.0165)}px "Share Tech Mono", monospace`;
    ctx.fillText(state.deathExplanation, w / 2, h * 0.555);
    ctx.globalAlpha = 1;
  }

  // ── Stats list ──
  // Uses the same data points as the legacy screen, but compact in one
  // block under the sprite. Score from grade also displayed.
  const runSecs  = Math.floor(survivalSeconds);
  const mins     = Math.floor(runSecs / 60);
  const secs     = runSecs % 60;
  const dmgTaken = Math.round(state.damageTakenThisRun ?? 0);

  const stats: Array<{ label: string; value: string; delay: number }> = [
    { label: 'TIME SURVIVED', value: `${mins}:${String(secs).padStart(2, '0')}`, delay: 2.4 },
    { label: 'LEVEL REACHED', value: String(levelReached),                       delay: 2.7 },
    { label: 'KILLS',         value: String(killCount),                          delay: 3.0 },
    { label: 'DAMAGE DEALT',  value: damageDealt.toLocaleString(),               delay: 3.3 },
    { label: 'DAMAGE TAKEN',  value: dmgTaken.toLocaleString(),                  delay: 3.6 },
    { label: 'CHESTS OPENED', value: String(state.chestsOpenedThisRun ?? 0),     delay: 3.9 },
    // GRADE SCORE moved to the big headline under RIP (BETA 21.12) — no dup here.
  ];

  // BETA 16.05 — sized off minDim + repositioned to give more breathing room.
  ctx.font = `${Math.round(minDim * 0.018)}px "Share Tech Mono", monospace`;
  const statStartY = h * 0.57;
  const lineH = minDim * 0.028;
  for (let i = 0; i < stats.length; i++) {
    const s = stats[i];
    const progress = Math.max(0, Math.min(1, (elapsed - s.delay) / 0.35));
    if (progress <= 0) continue;
    const slideY = statStartY + i * lineH + (1 - progress) * 18;
    ctx.globalAlpha = progress;
    // Label
    ctx.textAlign = 'right';
    ctx.fillStyle = '#666';
    ctx.fillText(s.label + ' :', w / 2 - 12, slideY);
    // Value
    ctx.textAlign = 'left';
    ctx.fillStyle = '#dddddd';
    ctx.fillText(s.value, w / 2 + 12, slideY);
  }
  ctx.globalAlpha = 1;

  // ── Trash talk cycler ──
  // Show one line at a time, ~10s per line, fade in/out. Starts after
  // stats finish (~5s in) so they don't compete for attention.
  const trashStartT = 5.0;
  const linePerLine = 10.0; // 10s per line on screen
  if (elapsed > trashStartT && _trashLines.length > 0) {
    const tIntoTrash = elapsed - trashStartT;
    const lineIdx = Math.floor(tIntoTrash / linePerLine) % _trashLines.length;
    const lineProgress = (tIntoTrash % linePerLine) / linePerLine; // 0..1 within current line
    // Fade in 0-15%, hold 15-85%, fade out 85-100%
    let lineAlpha = 1;
    if (lineProgress < 0.15) lineAlpha = lineProgress / 0.15;
    else if (lineProgress > 0.85) lineAlpha = (1 - lineProgress) / 0.15;
    ctx.globalAlpha = lineAlpha;
    ctx.textAlign = 'center';
    ctx.fillStyle = '#f5e6a8';
    ctx.font = `italic ${Math.round(minDim * 0.024)}px "Share Tech Mono", monospace`;
    // Wrap if line is too wide
    const maxW = w * 0.85;
    // BETA 16.05 — substitute {kills} / {seconds} / {character} / {cause}
    // tokens with the run's actual values so the line is never out of
    // sync with the stats panel (Dylan saw "Twelve kills?" while stats
    // showed 16 kills).
    let text = _trashLines[lineIdx].text;
    text = text
      .replace(/\{kills\}/g, String(killCount))
      .replace(/\{seconds\}/g, String(Math.round(survivalSeconds)))
      .replace(/\{character\}/g, characterName)
      .replace(/\{cause\}/g, state.killedByName ?? causeType);
    const textW = ctx.measureText(text).width;
    // BETA 16.05 — moved trash talk down so it sits between stats + buttons.
    const yMid = h * 0.78;
    if (textW <= maxW) {
      ctx.fillText(text, w / 2, yMid);
    } else {
      // Simple word-wrap: break at spaces, fit within maxW
      const words = text.split(' ');
      let lineBuf = '';
      const linesArr: string[] = [];
      for (const word of words) {
        const test = lineBuf ? `${lineBuf} ${word}` : word;
        if (ctx.measureText(test).width > maxW) {
          if (lineBuf) linesArr.push(lineBuf);
          lineBuf = word;
        } else {
          lineBuf = test;
        }
      }
      if (lineBuf) linesArr.push(lineBuf);
      const lh = Math.round(minDim * 0.030);
      const startY = yMid - ((linesArr.length - 1) * lh) / 2;
      for (let li = 0; li < linesArr.length; li++) {
        ctx.fillText(linesArr[li], w / 2, startY + li * lh);
      }
    }
    // Attribution
    ctx.fillStyle = '#888866';
    ctx.font = `italic ${Math.round(minDim * 0.016)}px "Share Tech Mono", monospace`;
    ctx.fillText('— Peanut', w / 2, h * 0.815);
    ctx.globalAlpha = 1;
  }

  // ── REPLAY / QUIT buttons (appear at 3 seconds) ──
  const rects = getDeathScreenButtonRects(w, h, elapsed);
  if (rects.visible) {
    const btnAlpha = Math.max(0, Math.min(1, (elapsed - BUTTON_DELAY_SEC) / 0.5));
    ctx.globalAlpha = btnAlpha;
    drawButton(ctx, rects.replay.x, rects.replay.y, rects.replay.w, rects.replay.h, 'REPLAY', '#5db073');
    drawButton(ctx, rects.quit.x,   rects.quit.y,   rects.quit.w,   rects.quit.h,   'QUIT',   '#b05050');
    // BETA 16.93 — Controller phase 3: focus box overlay on the
    // gamepad-targeted button so dpad/stick selection is unmistakable.
    if (getActiveScreen() === 'gameover_screen') {
      const focused = getFocusedTarget();
      if (focused?.id === 'replay' || focused?.id === 'quit') {
        const r = focused.id === 'replay' ? rects.replay : rects.quit;
        ctx.save();
        ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
        ctx.shadowBlur = 22;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 4;
        ctx.strokeRect(r.x - 4, r.y - 4, r.w + 8, r.h + 8);
        ctx.shadowBlur = 0;
        ctx.strokeStyle = '#ffcc00';
        ctx.lineWidth = 2;
        ctx.strokeRect(r.x - 1, r.y - 1, r.w + 2, r.h + 2);
        ctx.restore();
      }
    }
    ctx.globalAlpha = 1;
  }
  // ctx.restore() handled by the renderDeathScreen wrapper above.
}

function drawButton(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  label: string,
  edgeColor: string,
): void {
  // Solid black background with colored border + glow
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = edgeColor;
  ctx.lineWidth = 2;
  ctx.shadowColor = edgeColor;
  ctx.shadowBlur = 12;
  ctx.strokeRect(x, y, w, h);
  ctx.shadowBlur = 0;
  // Label
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${Math.round(h * 0.36)}px "Share Tech Mono", monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, x + w / 2, y + h / 2);
}

/** Hit-test a mouse click against the death screen buttons. Returns
 *  'replay' / 'quit' / null. The caller wires the actual restart /
 *  back-to-menu transitions (in GameCanvas.tsx). */
export function hitTestDeathScreenButton(
  mx: number, my: number,
  canvasW: number, canvasH: number,
  elapsedSec: number,
): 'replay' | 'quit' | null {
  const rects = getDeathScreenButtonRects(canvasW, canvasH, elapsedSec);
  if (!rects.visible) return null;
  if (mx >= rects.replay.x && mx <= rects.replay.x + rects.replay.w &&
      my >= rects.replay.y && my <= rects.replay.y + rects.replay.h) {
    return 'replay';
  }
  if (mx >= rects.quit.x && mx <= rects.quit.x + rects.quit.w &&
      my >= rects.quit.y && my <= rects.quit.y + rects.quit.h) {
    return 'quit';
  }
  return null;
}
